import { Check as CheckIcon, Fan } from "@phosphor-icons/react";
import { Check, Circle, Ear, HelpCircle, Loader2, Wifi, X } from "lucide-react";
import { useEffect, useState } from "react";

import {
  type GatewayDiagnosis,
  getApiErrorMessage,
  type RecordIssueRequest,
  useActivateTurbo,
  useGatewayDiagnosis,
  useGateways,
  useProvisionGateway,
  useRecordIssue,
  useVentilationState,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { useAssistant } from "./assistant-context";
import {
  AppBar,
  DiagnosisNotice,
  MaterialsNeeded,
  PhaseDots,
  PillButton,
  SkipFailedStep,
  StuckHelp,
  useRetryGate,
} from "./components";
import {
  describeMacMismatchAnswer,
  describeWhatWasTried,
  isEscalation,
  isLinkFault,
  pickGatewayDiagnosis,
  pickNoticeDiagnosis,
} from "./connector-issue";
import { PRODUCT_NAMES } from "./product-names";
import { unitReadable } from "./ventilation-readiness";

const VENTILATION_CONNECTOR = PRODUCT_NAMES.ventilationConnector;

/**
 * Phase 5 · Ventilation (Nilan) — Figma 1075:1555 (intro), 717:1246 (connect
 * the gateway), 717:1276 (connecting), 717:1317 (turbo intro), 717:1337
 * (turbo listen), 717:1364 (done).
 *
 * The Nilan is the home's single ventilation unit. A preconfigured USR gateway
 * replaces its WiFi dongle and bridges it to the Home Assistant hub (same
 * pattern as the Wavin floor-heating unit).
 *
 * Real wiring: "It's connected" provisions the Nilan gateway on the hub
 * (usr_provision, MAC from the household's preconfig), the connecting stage
 * polls until the gateway entry is loaded, and TURBO drives the Nilan's
 * fan-level register ("top" = level 4) through the backend. "Yes, I can hear
 * it" puts the fan back to whatever level the unit was already running.
 */

/** Ventilation's flat module block — brand Background Green. See the tint map
 *  in globals.css; the same colour marks this module on the guide. */
const MODULE_HERO = "bg-tint-green";

/**
 * The module's flat hero: a fan on the ventilation tint, spinning while the
 * unit is in TURBO and still once the step is done. Replaces two rendered
 * illustrations from the pre-block brand round (orange rings, gradients, a
 * stock green tick) that no other screen still looked like. Brand Black on the
 * module tint, like every other hero block; motion carries "full speed".
 */
function FanHero({ spinning, done = false }: { spinning: boolean; done?: boolean }) {
  return (
    <div
      className={cn(
        MODULE_HERO,
        "relative flex h-[240px] w-full items-center justify-center rounded-2xl",
      )}
    >
      <Fan
        weight="light"
        className={cn(
          "text-primary size-[132px]",
          spinning && "motion-safe:animate-spin motion-safe:[animation-duration:2.4s]",
        )}
        aria-hidden
      />
      {done && (
        <div className="bg-success absolute right-6 bottom-6 flex size-10 items-center justify-center rounded-full">
          <CheckIcon weight="bold" className="size-5 text-white" aria-hidden />
        </div>
      )}
    </div>
  );
}

/** Poll cadence while waiting for the gateway entry to load. */
const GATEWAY_POLL_MS = 3000;

/**
 * How long the connect may run before we offer the assistant. Matches
 * compartment 4, which provisions the same USR gateway: five minutes is past
 * the common case and well short of the fifteen-minute ceiling, so it never
 * reads as "this has failed".
 */
const CONNECT_PATIENCE_MS = 5 * 60 * 1000;

/**
 * How long the connect may run before the skip appears.
 *
 * Much shorter than the help button's window, and deliberately so: this is the
 * escape hatch for a connector the hub will never discover, and the person
 * waiting is usually standing in someone's home with the rest of the install
 * still to do. Offering the way out early costs a resident nothing (the CTA is
 * quiet, and the spinner keeps going) and saves an installer four minutes of
 * watching a spinner they already know is stuck.
 */
const SKIP_PATIENCE_MS = 60 * 1000;

/**
 * Automatic retries of a failed provision before the background diagnosis
 * starts. Not a ceiling on retrying itself — see the retry effect below,
 * which keeps going past this — only on when the diagnosis call fires and
 * how the eventual issue report describes the attempt count.
 */
const DIAGNOSIS_THRESHOLD = 3;

/**
 * Backoff between automatic provision retries, so a connector that is never
 * going to register does not get hammered with requests while the resident
 * watches the checklist spin.
 */
const PROVISION_RETRY_DELAY_MS = 5000;

/**
 * How long the connector must stay confirmedly failing before the gentle
 * "Still waiting?" prompt escalates to "We've noticed an issue". Past
 * DIAGNOSIS_THRESHOLD's own worst case (three retries at
 * PROVISION_RETRY_DELAY_MS apiece), so the button offering the diagnosis
 * never appears before that diagnosis has had a chance to come back.
 */
const CONNECT_FAILED_WINDOW_MS = 20 * 1000;

/**
 * How long the hub may go without a single reading from the ventilation unit
 * after the connector's entry has loaded, before the screen treats the connect
 * as failing and offers help and the skip.
 *
 * The Nilan's registers are polled once a minute, so a healthy unit shows a
 * reading within about a minute of the entry loading. Three minutes is past
 * that with room for one dropped poll, and short enough that a resident is
 * not left watching a spinner over a line that will never answer.
 */
const READINGS_PATIENCE_MS = 3 * 60 * 1000;

/** What the screen and the report say when the bridge is up but silent. */
const NO_READINGS_YET = `The ${PRODUCT_NAMES.ventilation} connector is on the network, but the hub has not received a reading from the ventilation unit yet.`;

/**
 * Failed TURBO attempts before the skip is offered. The first failure gets
 * the retry copy and the assistant; on Beta Test House 20 roughly every other
 * attempt got through, so one more tap is the fix far more often than a skip.
 */
const TURBO_TRIES_BEFORE_SKIP = 2;

/** Discovery steps shown while the gateway is provisioned and comes up. */
const CONNECT_STEPS = [
  "Ventilation Connector registered",
  "Verifying connection",
  "Ventilation online",
];

/**
 * Fallback fan level, used only when the unit reported no mode of its own
 * before TURBO (a dead modbus link reads as null). Houses do not share a
 * normal level — the first real unit tested was running "low" — so the mode
 * observed beforehand is always preferred.
 *
 * The fallback is deliberately the LOWEST setting: guessing too low is quiet
 * and cheap and the resident can raise it, whereas guessing too high leaves a
 * house running louder and more expensively than they chose, which nobody
 * would think to check.
 */
const FALLBACK_FAN_MODE = "low";

/**
 * Every stage of this module, in order — the dev screen gallery walks it, and
 * `Stage` is derived from it so the two can never disagree. Add a stage here
 * and it is in the gallery, and `STAGE_DOTS` below stops compiling until it has
 * a dot; there is no second list to remember.
 */
export const STAGES = [
  "intro",
  "connect",
  // The cable is its own screen, not a numbered line under the power step.
  // Two houses failed this exact hookup while it was one step among four:
  // at Johan's the gateway was plugged into the WiFi module instead of
  // replacing it. One screen: the plate with the socket circled, the attic
  // photo, two actions.
  "cable",
  "connecting",
  // The hub is provisioned for a different device than the one on our
  // record (VLX-111). Reached from "connecting", so it shares that dot
  // rather than claiming a step of its own — it is a bail-out, not
  // progress.
  "mac-mismatch",
  "turbo-intro",
  "turbo-listen",
  "done",
] as const;

export type Stage = (typeof STAGES)[number];

/**
 * Which dot is lit for each stage. The dots track progress *within this
 * compartment* (the pattern the hub and windows modules already use), so they
 * actually advance as the user works — they previously sat frozen on one index
 * across all seven screens, which made the indicator read as decoration. A
 * bail-out stage (like "mac-mismatch") reuses the index of the stage it
 * branches from rather than claiming a new one.
 */
const STAGE_DOTS: Record<Stage, number> = {
  intro: 0,
  connect: 1,
  cable: 2,
  connecting: 3,
  "mac-mismatch": 3,
  "turbo-intro": 4,
  "turbo-listen": 5,
  done: 6,
};

// The highest index plus one, not the number of entries: a bail-out stage
// shares its index with the one it branches from, so counting keys would add
// a phantom dot nothing ever lights.
const VENT_STAGE_TOTAL = Math.max(...Object.values(STAGE_DOTS)) + 1;

interface VentilationScreenProps {
  busy: boolean;
  error: string;
  /** True while the skip is in flight, so its button can't be double-tapped. */
  skipping?: boolean;
  /** Records this compartment as skipped, carrying the error that caused it. */
  onSkip?: (reason: string) => void;
  onComplete: () => void;
  /**
   * Stage to open on. Production always omits it (each module starts at its
   * own first stage); the dev screen gallery uses it to deep-link a stage.
   */
  initialStage?: Stage;
  /**
   * A stand-in verdict for the dev screen gallery, so both notices on the
   * connecting stage can be reviewed without a broken connector to hand.
   * Production always omits it: the real verdict comes from the background
   * diagnosis, and this one deliberately outranks it so the gallery is not at
   * the mercy of whatever the office hub is doing.
   */
  previewDiagnosis?: GatewayDiagnosis;
  /**
   * True only in the dev screen gallery. Nothing this screen records (issues,
   * skips) is written while it is set, so a designer clicking through never
   * leaves rows on a real household's report. Production never passes it.
   */
  preview?: boolean;
}

export function VentilationScreen({
  busy,
  error,
  skipping = false,
  onSkip,
  onComplete,
  initialStage,
  previewDiagnosis,
  preview = false,
}: VentilationScreenProps) {
  const [stage, setStage] = useState<Stage>(initialStage ?? "intro");
  const [turboError, setTurboError] = useState("");
  // Failed TURBO taps since the TURBO screen was entered, gating when the
  // skip shows. Cleared on entering the screen and on a success, so a
  // resident who loops back from "did you hear it?" or returns from another
  // stage starts over rather than inheriting an old failure.
  const [turboFailures, setTurboFailures] = useState(0);
  useEffect(() => {
    if (stage === "turbo-intro") {
      setTurboFailures(0);
    }
  }, [stage]);
  // The level the unit was running before TURBO, so it goes back exactly there.
  const [previousFanMode, setPreviousFanMode] = useState<string | null>(null);
  // Every provision attempt that has failed since the last tap of "The cable
  // is in" (or the last success). Latched rather than derived from the
  // mutation's own isError, which flips false the instant a retry goes
  // pending — see the retry effect below.
  const [failedAttempts, setFailedAttempts] = useState(0);
  // Set when the resident took the cable screen's exit ("Not sure which
  // socket? Ask with a photo"). It records an issue and opens the chat, and
  // from then on the skip is offered, as it is after a failed connect.
  const [askedWhichSocket, setAskedWhichSocket] = useState(false);
  const assistant = useAssistant();
  // Which connect attempt this is, counted from each tap of "The cable is
  // in". The unit's readings are cached per attempt, so a reading from an
  // earlier try on this same screen can never tick the checklist for a
  // later one.
  const [connectAttempt, setConnectAttempt] = useState(0);
  // Snapshot of the diagnosis that sent us to "mac-mismatch", taken at the
  // moment of the transition. `activeDiagnosis` itself goes undefined the
  // instant `stage` stops being "connecting" (its query is only enabled on
  // that stage), so the mismatch screen and the issue it records both read
  // this snapshot rather than the live (by then empty) value.
  const [mismatchDiagnosis, setMismatchDiagnosis] = useState<GatewayDiagnosis | undefined>(
    undefined,
  );

  const provision = useProvisionGateway();
  const turbo = useActivateTurbo();
  const recordIssue = useRecordIssue();

  const provisionOk =
    provision.data != null &&
    (provision.data.status === "created" || provision.data.status === "already_configured");
  const provisionFailed =
    provision.isError || (provision.data != null && provision.data.status === "error");

  // Poll the gateway list once the entry exists, until it reports loaded.
  const gateways = useGateways({
    enabled: stage === "connecting" && provisionOk,
    refetchInterval: GATEWAY_POLL_MS,
  });
  // The gateway WE just provisioned, by MAC, not merely one whose title
  // mentions "modbus:": a MAC change leaves the old entry behind, still
  // loaded, so matching on title alone reports success for a connector that
  // was never found (VLX-111). `provision.data` always carries the MAC by the
  // time this query runs, since `provisionOk` requires it.
  const entry = gateways.data?.find((e) => e.mac === provision.data?.mac);
  const gatewayLoaded = entry?.state === "loaded";

  // An entry loading says the bridge is on the network, not that the unit
  // behind it has answered. Poll the unit's own readings once the entry is
  // up, and hold the checklist's last tick for a real one: Beta Test House
  // 20 was offered TURBO 26 seconds after the entry loaded, before the hub
  // had read a single register, and the command failed.
  const ventilation = useVentilationState({
    enabled: stage === "connecting" && provisionOk && gatewayLoaded,
    refetchInterval: GATEWAY_POLL_MS,
    attempt: connectAttempt,
  });
  // Latched: once the hub has read the unit during this attempt, the link is
  // proven and the tick stays. The cache is keyed by attempt, so there is no
  // earlier reading to inherit, and a later dropped poll or a flap back to
  // null (a noisy line, as at Beta Test House 20) does not un-tick a
  // checklist the resident has already watched complete. TURBO has its own
  // guard and retry for the moment of the command.
  const [unitSeen, setUnitSeen] = useState(false);
  useEffect(() => {
    if (unitReadable(ventilation.data)) {
      setUnitSeen(true);
    }
  }, [ventilation.data]);
  const readable = unitSeen;
  const readingsStalled =
    useRetryGate(stage === "connecting" && gatewayLoaded && !readable, READINGS_PATIENCE_MS) ===
    "failed";

  // Runs in the background as soon as either question below needs answering,
  // well before anything about it reaches the resident, so the verdict already
  // exists by the time the "We've noticed an issue" button or a skip needs it.
  //
  // Two different questions get asked. After repeated failures it is "why will
  // this not register?". On an apparently clean connect it is "is the Nilan
  // actually answering?", which nothing else on this screen can tell: there is
  // no zone list here to come back empty, so the verdict is the only evidence
  // available.
  const diagnosisEligible =
    stage === "connecting" &&
    ((failedAttempts >= DIAGNOSIS_THRESHOLD && !provisionOk) || (provisionOk && gatewayLoaded));
  const diagnosis = useGatewayDiagnosis({ enabled: diagnosisEligible });
  // Gated on the same eligibility the query itself uses, not just on
  // `provisionOk`: a query's cached data outlives `enabled` flipping off, so
  // a fresh attempt that hasn't failed three times yet must not show or
  // record the verdict from an earlier, already-abandoned run.
  const activeDiagnosis = previewDiagnosis
    ? previewDiagnosis
    : diagnosisEligible
      ? pickGatewayDiagnosis(diagnosis.data, "nilan")
      : undefined;
  // The verdict to put on screen, and whether it is one nobody in the house
  // can act on. Both live here rather than at the call site so the connecting
  // stage reads as one decision instead of three nested conditions.
  const noticeDiagnosis = pickNoticeDiagnosis(activeDiagnosis);
  const escalating = isEscalation(activeDiagnosis);

  // The last checklist item waits for the hub's own verdict on whether the
  // Nilan is answering, not merely for the modbus entry to load. An entry
  // loads, and stays loaded, whether or not anything is on the other end of
  // the RS-485 pair, so on its own it would tick green over a connector that
  // has been silent for a week. `isLinkFault` fails open, so a diagnosis that
  // cannot be reached never holds a household up.
  const linkFault = isLinkFault(activeDiagnosis);
  const linkChecked = !diagnosisEligible || diagnosis.isSuccess || diagnosis.isError;
  const connectStep = provisionOk
    ? gatewayLoaded && linkChecked && !linkFault && readable
      ? CONNECT_STEPS.length
      : 1
    : 0;
  // All three checklist items green. The household watched that happen, so
  // it waits for their own tap of Continue rather than moving on for them.
  const readyToContinue = connectStep >= CONNECT_STEPS.length;

  // True for the whole stretch a connect attempt is failing, not just the
  // instant the mutation reports an error — see `failedAttempts` above.
  //
  // A confirmed link fault counts as failing even though nothing errored:
  // registering the connector says only that the bridge is on the network, not
  // that the unit behind it is answering. Without this the screen would sit on
  // a spinner with no way out, since the offer of help and the skip both hang
  // off this flag.
  //
  // A bridge that is up but has produced no reading for READINGS_PATIENCE_MS
  // counts too, for the same reason.
  const isConnectFailing =
    stage === "connecting" &&
    ((failedAttempts > 0 && !provisionOk) || linkFault || readingsStalled);
  // Confirms a raw "is it failing" signal into "has this been failing for a
  // while", the same gate hub-setup-screen uses for its own stuck-help offer.
  const retryState = useRetryGate(isConnectFailing, CONNECT_FAILED_WINDOW_MS);

  /**
   * Every issue this screen files, behind one gate.
   *
   * Fire-and-forget: a failure here must never block the skip it rides along
   * with. Silent in the dev gallery, which drives the diagnosis stages from a
   * fixture verdict rather than from hardware, so a designer clicking through
   * "Continue with the rest" to see the notice's CTA does not leave real rows
   * on a real household's report.
   */
  function reportIssue(body: RecordIssueRequest) {
    // `preview` is the gallery's explicit flag. The fixture verdict alone was
    // not enough: the cable stage has no diagnosis fixture, so a designer
    // tapping its exit link filed a real issue. Keying on `initialStage`
    // instead would break the day production deep-links a stage.
    if (preview || previewDiagnosis != null) {
      logger.debug("[onboarding] preview mode, issue not recorded", body);
      return;
    }
    recordIssue.mutate(body, {
      onError: (err) => logger.warn("[onboarding] could not record the connector issue", err),
    });
  }

  /** Records this connector giving up, so the end-of-onboarding report knows
   *  what was tried. */
  function recordConnectorIssue(stageName: string, reason: string) {
    reportIssue({
      step_key: "ventilation",
      stage: stageName,
      verdict: activeDiagnosis?.verdict,
      action: activeDiagnosis?.action,
      detail: activeDiagnosis?.detail ?? reason,
      // The diagnosis's supporting observations and the connector's address
      // are for the support report, never for the resident: the email's
      // evidence block is only as good as what is stored here.
      findings: activeDiagnosis?.findings ?? [],
      connector_address: activeDiagnosis?.address ?? undefined,
      // +1: `failedAttempts` counts retries dispatched, so the first tap
      // itself is one more try than that.
      what_was_tried: describeWhatWasTried(failedAttempts + 1, activeDiagnosis),
    });
  }

  /**
   * The cable screen's exit. Nothing has been plugged in, so there is no
   * diagnosis to attach: the record is the resident's own question, filed so
   * VELUX hears it from the app rather than from an email. The chat opens with
   * the question drafted, not sent, so a photo can go with it.
   */
  function askWhichSocket() {
    logger.debug("[onboarding] ventilation: resident asked which socket takes the blue cable");
    reportIssue({
      step_key: "ventilation",
      stage: "cable",
      verdict: "cable_destination_unclear",
      detail:
        "Could not tell which socket on the Nilan takes the blue cable. Opened the chat to send a photo.",
      what_was_tried: "Reached the cable screen and asked before plugging anything in.",
    });
    setAskedWhichSocket(true);
    assistant.open({
      stepKey: "ventilation",
      stage: "cable",
      draft:
        "I can't tell which socket on my Nilan the blue cable goes into. I will add a photo of the side of the unit.",
    });
  }

  /**
   * Record what went wrong on the connecting stage, then carry the household
   * past this compartment.
   *
   * One function behind two buttons on purpose. The timed skip and the escalate
   * notice's "Continue with the rest" do exactly the same thing; the only
   * difference is that the notice knows the verdict already, so it does not
   * make anyone sit out SKIP_PATIENCE_MS first to reach a conclusion we have
   * already drawn.
   */
  function continueWithoutConnector() {
    const reason =
      noticeDiagnosis?.detail ??
      (readingsStalled ? NO_READINGS_YET : entry?.reason) ??
      `The ${VENTILATION_CONNECTOR} never came online.`;
    recordConnectorIssue("connecting", reason);
    onSkip?.(reason);
  }

  /**
   * Give up on a unit that would not run TURBO, and say so in the report.
   *
   * It used to skip silently, which is the one case where that is most
   * misleading: everything up to here succeeded, so a compartment marked
   * skipped with no issue beside it reads as a resident who chose to move on
   * rather than a unit that refused the command. Its own verdict string keeps
   * the upsert key clear of the connecting stage's report.
   */
  function skipFailedTurbo() {
    reportIssue({
      step_key: "ventilation",
      stage: "turbo_failed",
      verdict: "turbo_failed",
      detail: turboError,
      what_was_tried: `The ${VENTILATION_CONNECTOR} registered and the hub reached it. Running the fan up to full speed failed.`,
    });
    onSkip?.(turboError);
  }

  /**
   * Record which way the household answered the mac-mismatch question, then
   * move on. Neither answer is ours to act on directly (VLX-111 explains why:
   * three real situations produce this same finding and need opposite fixes),
   * so both ride the same skip path the rest of this screen uses to carry the
   * household past a compartment that cannot succeed from here.
   */
  function answerMacMismatch(matches: boolean) {
    reportIssue({
      step_key: "ventilation",
      stage: "mac_mismatch",
      verdict: mismatchDiagnosis?.verdict,
      action: mismatchDiagnosis?.action,
      detail: mismatchDiagnosis?.detail,
      findings: mismatchDiagnosis?.findings ?? [],
      connector_address: mismatchDiagnosis?.address ?? undefined,
      what_was_tried: `${describeWhatWasTried(failedAttempts + 1, mismatchDiagnosis)} ${describeMacMismatchAnswer(matches)}`,
    });
    onSkip?.(
      mismatchDiagnosis?.detail ??
        "The hub is provisioned for a different device than the one on record.",
    );
  }

  // Retry a failed provision automatically instead of bouncing the resident
  // back to an error screen: a USR gateway often needs more than one attempt,
  // and every retry happens while the checklist keeps spinning. The count
  // only gates the background diagnosis and the "We've noticed an issue"
  // button (via `retryState` above) — it never stops the retries themselves.
  useEffect(() => {
    if (stage !== "connecting" || !provisionFailed) {
      return;
    }
    const timer = setTimeout(() => {
      setFailedAttempts((n) => n + 1);
      provision.mutate({ kind: "nilan" });
    }, PROVISION_RETRY_DELAY_MS);
    return () => clearTimeout(timer);
    // `provision` is a stable mutation object; depending on it would re-fire
    // this effect on every state change instead of only on a new failure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, provisionFailed]);

  // Connecting: the diagnosis says our record and the hub disagree on which
  // device this is. That is not something retrying fixes, so it gets its own
  // stage rather than leaving the checklist spinning on a gateway that will
  // never load (VLX-111). The verdict is snapshotted here because leaving
  // "connecting" disables the diagnosis query behind it.
  useEffect(() => {
    if (stage !== "connecting" || activeDiagnosis?.verdict !== "mac_mismatch") {
      return;
    }
    setMismatchDiagnosis(activeDiagnosis);
    setStage("mac-mismatch");
  }, [stage, activeDiagnosis]);

  function startConnecting() {
    setFailedAttempts(0);
    setConnectAttempt((n) => n + 1);
    setUnitSeen(false);
    provision.mutate({ kind: "nilan" });
    setStage("connecting");
  }

  async function activateTurbo() {
    setTurboError("");
    try {
      const result = await turbo.mutateAsync({});
      logger.debug("[onboarding] TURBO activated", result);
      setTurboFailures(0);
      // Captured before the write, so this is the resident's own setting.
      setPreviousFanMode(result?.previous_fan_mode ?? null);
      setStage("turbo-listen");
    } catch (err) {
      setTurboFailures((n) => n + 1);
      setTurboError(getApiErrorMessage(err, "Could not reach the ventilation unit."));
    }
  }

  function confirmHeard() {
    // Put the unit back exactly where the resident had it. A failure here
    // shouldn't block the flow, but it must not be silent either.
    const restoreTo = previousFanMode ?? FALLBACK_FAN_MODE;
    logger.debug("[onboarding] restoring ventilation to", restoreTo);
    turbo.mutate(
      { fan_mode: restoreTo },
      { onError: (err) => logger.warn("[onboarding] could not restore fan level", err) },
    );
    setStage("done");
  }

  // ── Stage 0: intro — "Setting up Ventilation" + the kit (Figma 1075:1555)
  if (stage === "intro") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="ventilation" stage={stage} title={PRODUCT_NAMES.ventilation} />
        <PhaseDots total={VENT_STAGE_TOTAL} activeIndex={STAGE_DOTS[stage]} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          {/* The two things that meet, not a schematic. The connector's own
              blue cable already points at the Nilan; drawing a second cable,
              or a dashed line for a wireless link, only confused the review.
              The SmartClimate Hub is named in the sentence below, not drawn. */}
          <div
            className={`flex h-[183px] shrink-0 items-end justify-center gap-4 overflow-hidden rounded-2xl px-4 pt-4 pb-3 ${MODULE_HERO}`}
          >
            <figure className="flex flex-1 flex-col items-center gap-2 text-center">
              <img
                src="/brand/onboarding/ventilation-connector-shipped.png"
                alt={`The ${VENTILATION_CONNECTOR} as it ships, with its blue cable already wired in`}
                className="h-[104px] w-auto max-w-full object-contain"
              />
              <figcaption className="text-primary text-[13px] leading-4 font-medium">
                {VENTILATION_CONNECTOR}
              </figcaption>
            </figure>
            <figure className="flex flex-1 flex-col items-center gap-2 text-center">
              <img
                src="/brand/onboarding/ventilation-nilan.png"
                alt="Your Nilan ventilation unit"
                className="h-[104px] w-auto max-w-full object-contain"
              />
              <figcaption className="text-primary text-[13px] leading-4 font-medium">
                Your Nilan
              </figcaption>
            </figure>
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Setting up Ventilation</h1>
            <p className="text-muted text-[17px] leading-6">
              <span className="font-bold">Why this part?</span> Your Nilan already moves air through
              the house. Once it&apos;s connected, SmartClimate speeds it up when the air needs it
              and eases off when it doesn&apos;t.
            </p>
            <p className="text-muted text-[17px] leading-6">
              The {VENTILATION_CONNECTOR} plugs into your Nilan where its own Wi-Fi module is
              plugged in today, and reaches the {PRODUCT_NAMES.hub} over Wi-Fi. It needs a power
              socket near the unit.
            </p>
            <p className="text-muted text-[17px] leading-6">
              It&apos;s usually in the attic, so bring a light.
            </p>
          </div>
          <MaterialsNeeded
            items={[
              {
                title: VENTILATION_CONNECTOR,
                caption: "The box labelled NILAN - VENTILATION. Reads your Nilan for SmartClimate.",
                img: "/brand/onboarding/ventilation-connector-shipped.png",
                alt: `The ${VENTILATION_CONNECTOR}`,
              },
              {
                title: "Blue cable",
                caption: `Already wired into the ${VENTILATION_CONNECTOR}. Only the plug at the other end is yours to connect.`,
                img: "/brand/onboarding/ventilation-cable.png",
                alt: "The blue cable",
              },
              {
                title: "Power adapter",
                caption: `For the ${VENTILATION_CONNECTOR}.`,
                img: "/brand/onboarding/heating-adapter.png",
                alt: "The power adapter",
              },
              {
                title: "Power strip",
                caption: "In case the nearest socket is out of reach.",
                img: "/brand/onboarding/hub-kit-multioutlet.png",
                alt: "The power strip",
              },
            ]}
          />
          <p className="text-primary text-[17px] leading-6">
            Make sure you have everything ready before we start.
          </p>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton label="I have everything" onClick={() => setStage("connect")} />
        </div>
      </div>
    );
  }

  // ── Stage 1: connect — antenna on, power on ──────────────────────────────
  if (stage === "connect") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="ventilation"
          stage={stage}
          title={PRODUCT_NAMES.ventilation}
          onBack={() => setStage("intro")}
        />
        <PhaseDots total={VENT_STAGE_TOTAL} activeIndex={STAGE_DOTS[stage]} />
        <div className="flex flex-1 flex-col gap-[18px] px-6 pt-2">
          {/* The connector itself, LED panel forward: this screen's CTA is
              "the power light is on", so the hero shows where that light is.
              The Nilan appears on the next screen, where the resident turns
              to face it. The photo is the kit unit as shipped (Francis,
              2026-09-15), closing VLX-101: the manual's render showed a bare
              body residents could not match to the box in their hands. */}
          <div className={`flex h-[187px] items-center justify-center rounded-2xl ${MODULE_HERO}`}>
            <img
              src="/brand/onboarding/ventilation-connector-shipped.png"
              alt={`The ${VENTILATION_CONNECTOR} as it ships: antenna on, blue cable wired in, Power, Work and Link lights on the front`}
              className="h-[170px] object-contain"
            />
          </div>
          <div className="flex flex-col gap-2">
            <h1 className="text-primary type-headline">Power up the {VENTILATION_CONNECTOR}</h1>
            <p className="text-muted text-[17px] leading-6">
              The {VENTILATION_CONNECTOR} lets SmartClimate reach your Nilan. Set it up beside the
              unit first. The blue cable&apos;s other end comes on the next screen.
            </p>
          </div>
          <div className="flex flex-col gap-2.5">
            {[
              `If the antenna is not on yet, screw it onto the ${VENTILATION_CONNECTOR} until it stops turning. Finger-tight, but all the way in.`,
              // Look, do not tighten. "Check it is firmly wired" read as an
              // instruction to fix it, and in August the chat sent two houses
              // to swap the thin wires. The guard now blocks that answer; the
              // screen must stop inviting it. No wire count: nobody has
              // confirmed how many the shipped cable carries, and it does not
              // matter to the check.
              `The small wires from the blue cable should sit firmly in the green plug on the ${VENTILATION_CONNECTOR}. Look only. If any of them is loose, tell the chat.`,
              // Beta House 11's lesson from the fan article, moved to the
              // moment the connector is placed.
              `Put the ${VENTILATION_CONNECTOR} near the Nilan. Point the antenna up, away from foil ducting and pipes.`,
              // Michael called his spare attic outlet "pure luck". The kit
              // ships a power strip for exactly this. Power is red when on
              // (field sheet, and his own words).
              `Plug the power adapter into the ${VENTILATION_CONNECTOR} and into a socket. Use the power strip from the kit if the socket is out of reach. The Power light comes on red.`,
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="bg-background flex size-6 shrink-0 items-center justify-center rounded-xl">
                  <span className="text-primary type-caption">{i + 1}</span>
                </div>
                <p className="text-muted text-[15px] leading-[22px]">{step}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-3.5 pb-6">
          <PillButton label="The power light is on" onClick={() => setStage("cable")} />
        </div>
      </div>
    );
  }

  // ── Stage 2: cable — the socket, shown on the plate and in the attic ─────
  if (stage === "cable") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="ventilation"
          stage={stage}
          title={PRODUCT_NAMES.ventilation}
          onBack={() => setStage("connect")}
        />
        <PhaseDots total={VENT_STAGE_TOTAL} activeIndex={STAGE_DOTS[stage]} />
        <div className="flex flex-1 flex-col gap-[18px] px-6 pt-2">
          <div className="flex flex-col gap-2">
            <h1 className="text-primary type-headline">Connect the blue cable to your Nilan</h1>
            {/* By function, never by colour: Michael's unit had two candidate
                sockets and Beta House 7's had a green six-pin plug where the
                overview shows a cream eight-pin one. */}
            <p className="text-muted text-[17px] leading-6">
              On the side of your Nilan is a metal plate with a few connections. You need the
              network socket: the small square one a network cable clicks into, like the one on your
              router.
            </p>
          </div>
          {/* Nilan's own overview of the plate, cropped and cleaned. The ring
              is ours, drawn over the photo in the image's own 1269 by 735
              coordinates, so nothing is baked in. A re-crop of the asset means
              re-placing the circle. */}
          <figure className="flex flex-col gap-1.5">
            <div className="relative">
              <img
                src="/brand/onboarding/ventilation-nilan-plate.jpg"
                width={1269}
                height={735}
                alt="The connection plate on the side of the Nilan"
                className="w-full rounded-2xl"
              />
              <svg
                viewBox="0 0 1269 735"
                preserveAspectRatio="none"
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 h-full w-full"
              >
                <circle cx="230" cy="500" r="112" fill="none" stroke="#2f6fde" strokeWidth="9" />
              </svg>
            </div>
            <figcaption className="text-muted text-[13px] leading-[18px]">
              The connection plate on the side of your Nilan. Yours may differ: fewer connections,
              other colours, more pins. The connection you need is circled in blue.
            </figcaption>
          </figure>
          {/* The reality check under the clean plate: a real attic, the
              white cable still in. Says where that cable goes, so unplugging
              it does not feel like breaking something. */}
          <figure className="flex flex-col gap-1.5">
            <img
              src="/brand/onboarding/ventilation-nilan-socket.jpg"
              alt="The network socket in a real attic, with a white cable already in it"
              className="max-h-[210px] w-full rounded-2xl object-cover object-[center_25%]"
            />
            <figcaption className="text-muted text-[13px] leading-[18px]">
              How it usually looks, circled in blue. A white cable might already be in the socket.
            </figcaption>
          </figure>
          <div className="flex flex-col gap-2.5">
            {[
              // "That socket", the sentence that would have stopped Johan
              // plugging the connector into the module.
              "If a cable is already in the network socket, unplug it and keep it. Its other end is the Nilan's own Wi-Fi module.",
              `Plug the blue cable from the ${VENTILATION_CONNECTOR} into that socket, until it clicks.`,
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="bg-background flex size-6 shrink-0 items-center justify-center rounded-xl">
                  <span className="text-primary type-caption">{i + 1}</span>
                </div>
                <p className="text-muted text-[15px] leading-[22px]">{step}</p>
              </div>
            ))}
          </div>
          {/* Michael asked this outright: how many months without sight of
              his unit. Visible, not hidden in the hint (Francis, 2026-09-15).
              No swap-back is offered; that is VELUX's call and would need the
              diagnosis to tolerate a temporary silence. */}
          <p className="text-primary text-[15px] leading-[22px]">
            The Nilan&apos;s own app cannot see the unit for as long as SmartClimate is connected.
            SmartClimate runs the fan through the {PRODUCT_NAMES.hub}. The unit itself keeps running
            as before.
          </p>
        </div>
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-3.5 pb-6">
          {provisionFailed && (
            <p role="alert" className="text-destructive text-center text-[15px]">
              {provision.data?.status === "error"
                ? (provision.data.detail ?? "The gateway could not be registered.")
                : getApiErrorMessage(provision.error, "The gateway could not be registered.")}
            </p>
          )}
          <PillButton
            label={provisionFailed ? "Try again" : "The cable is in"}
            onClick={startConnecting}
          />
          {/* Beta House 5, 7 and Michael all reached this screen, could not
              tell which socket, and emailed. This is the way to say so inside
              the app: it records the question and opens the chat with it
              drafted, so a photo can go with it. */}
          {!provisionFailed && (
            <PillButton
              variant="ghost"
              label="Not sure which socket? Ask with a photo"
              onClick={askWhichSocket}
            />
          )}
          {askedWhichSocket && !provisionFailed && onSkip && (
            <SkipFailedStep
              busy={skipping}
              onSkip={() =>
                onSkip("Could not tell which socket on the Nilan takes the blue cable.")
              }
            />
          )}
          {provisionFailed && (
            <StuckHelp
              stepKey="ventilation"
              stage="provision_failed"
              lastError={
                provision.data?.status === "error"
                  ? (provision.data.detail ?? "The gateway could not be registered.")
                  : getApiErrorMessage(provision.error, "The gateway could not be registered.")
              }
              seed={`Registering the ${VENTILATION_CONNECTOR} on my hub failed. What went wrong and what should I do?`}
              label="Ask what went wrong"
            />
          )}
          {provisionFailed && onSkip && (
            <SkipFailedStep
              busy={skipping}
              onSkip={() => {
                const reason =
                  provision.data?.status === "error"
                    ? (provision.data.detail ?? "The gateway could not be registered.")
                    : getApiErrorMessage(provision.error, "The gateway could not be registered.");
                recordConnectorIssue("provision_failed", reason);
                onSkip(reason);
              }}
            />
          )}
        </div>
      </div>
    );
  }

  // ── Stage 3: connecting — provision the gateway and wait for it to load ──
  if (stage === "connecting") {
    // What the screen knows about why this is still going: the verdict if
    // one has landed, the hub's own words for the entry if not. Hoisted so
    // the caption and the assistant read from the same place, and so the
    // reassurance below (which is not an error) never travels as one.
    const connectingReason =
      activeDiagnosis?.detail ?? (readingsStalled ? NO_READINGS_YET : entry?.reason) ?? undefined;
    const captionText =
      connectingReason ??
      "This can take up to fifteen minutes. Keep the app open and leave everything plugged in.";
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="ventilation"
          stage={stage}
          title={PRODUCT_NAMES.ventilation}
          onBack={() => setStage("cable")}
        />
        <PhaseDots total={VENT_STAGE_TOTAL} activeIndex={STAGE_DOTS[stage]} />
        <div className="flex flex-1 flex-col items-center gap-10 px-6 py-10">
          {/* Once the verdict says nobody in the house can bring this
              connector up, the hero stops pretending to wait: the ring stops
              pulsing and the Wi-Fi glyph becomes the red cross the rest of
              the flow uses for a step that did not happen. A fixable verdict
              keeps the pulse, because there the connector may still come up
              once the resident has acted. */}
          <div className="relative flex size-[180px] items-center justify-center">
            <div
              className={cn(
                "bg-accent-light/40 absolute inset-0 rounded-full",
                !escalating && "animate-ping",
              )}
            />
            <div className="bg-accent-light/60 absolute inset-5 rounded-full" />
            <div className="border-border bg-background relative flex size-20 items-center justify-center rounded-[40px] border">
              {escalating ? (
                <X className="text-destructive size-10" strokeWidth={2.5} aria-hidden />
              ) : (
                <Wifi className="text-accent size-10" strokeWidth={2.5} />
              )}
            </div>
          </div>
          <div className="flex flex-col gap-3 text-center">
            {/* The title stops saying "connecting" the moment the verdict says
                nobody in the house can finish it. The subtitle names the
                outcome once and points at the notice rather than repeating it. */}
            {escalating ? (
              <>
                <h1 className="text-primary type-headline">We could not connect to your Nilan</h1>
                <p className="text-muted text-[17px] leading-6">
                  The {VENTILATION_CONNECTOR} is registered, but your hub cannot reach it. The
                  details are below.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-primary type-headline">Connecting to your Nilan…</h1>
                <p className="text-muted text-[17px] leading-6">
                  We&apos;re registering the {VENTILATION_CONNECTOR} and checking it can reach the
                  unit.
                </p>
              </>
            )}
          </div>
          <div className="flex w-full flex-col gap-4 px-5">
            {CONNECT_STEPS.map((label, i) => {
              const done = i < connectStep;
              const active = i === connectStep;
              return (
                <div key={label} className="flex items-center gap-3">
                  {done ? (
                    <Check className="text-success size-5 shrink-0" strokeWidth={3} />
                  ) : active && escalating ? (
                    <X className="text-destructive size-5 shrink-0" strokeWidth={3} aria-hidden />
                  ) : active ? (
                    <Loader2 className="text-accent size-5 shrink-0 animate-spin" />
                  ) : (
                    <Circle className="text-border size-5 shrink-0" />
                  )}
                  <p
                    className={cn(
                      "text-[17px] leading-6",
                      done || active ? "text-primary" : "text-muted",
                    )}
                  >
                    {active && !escalating ? `${label}…` : label}
                  </p>
                </div>
              );
            })}
          </div>
          {readyToContinue ? (
            <p className="text-muted type-body-sm text-center">
              Your Nilan is connected. Tap continue to run TURBO.
            </p>
          ) : (
            <>
              {/* Same USR gateway as compartment 4, same fifteen-minute
                  ceiling. See the note there: the average was the wrong
                  number to show, because the only lever a waiting resident
                  has is the power cable and pulling it mid-provision is what
                  actually does damage.

                  The whole line goes once a notice is up, not just its
                  verdict: everything this caption can fall back to contradicts
                  the notice. The hub's own reason is a raw Home Assistant
                  string, and the reassurance is an invitation to keep waiting
                  for a connector we have just said nobody in the house can
                  bring up. */}
              {!noticeDiagnosis && (
                <p className="text-muted type-body-sm text-center">{captionText}</p>
              )}
              {/* A verdict outranks both timers. The notice carries its own
                  action, so the generic prompt stands down rather than
                  offering a second, vaguer way into the same assistant. */}
              {noticeDiagnosis ? (
                <DiagnosisNotice
                  diagnosis={noticeDiagnosis}
                  stepKey="ventilation"
                  stage="connecting"
                  connectorName={VENTILATION_CONNECTOR}
                  busy={skipping}
                  onContinue={onSkip && (() => continueWithoutConnector())}
                />
              ) : (
                <StuckHelp
                  stepKey="ventilation"
                  stage="connecting"
                  afterMs={CONNECT_PATIENCE_MS}
                  lastError={connectingReason}
                  seed={`I'm waiting for the ${VENTILATION_CONNECTOR} to connect and it's taking a long time. The antenna is screwed all the way in. What should I check?`}
                  label="Still waiting? Get help"
                  hasFailed={retryState === "failed"}
                  escalateLabel="We've noticed an issue"
                  escalateSeed={`The ${VENTILATION_CONNECTOR} still hasn't connected after several tries. What should I check?`}
                />
              )}
              {/* This stage does not fail, it just never finishes: a
                  connector the hub cannot discover leaves the spinner up for
                  as long as anyone is willing to watch it. Hidden while an
                  escalate verdict is up: the notice already offers the way on,
                  and two skips on one screen is a choice between identical
                  doors. Hidden rather than unmounted so a later poll that flips
                  the verdict back to an on-site fault hands back a timer that
                  has kept running. */}
              {onSkip && (
                <SkipFailedStep
                  busy={skipping}
                  afterMs={SKIP_PATIENCE_MS}
                  hidden={escalating}
                  onSkip={() => continueWithoutConnector()}
                />
              )}
            </>
          )}
        </div>
        {readyToContinue && (
          <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
            <PillButton label="Continue" onClick={() => setStage("turbo-intro")} />
          </div>
        )}
      </div>
    );
  }

  // ── MAC mismatch · the hub found a different device than we expected ─────
  if (stage === "mac-mismatch") {
    const answering = recordIssue.isPending || skipping;
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="ventilation" stage={stage} title={PRODUCT_NAMES.ventilation} />
        <PhaseDots total={VENT_STAGE_TOTAL} activeIndex={STAGE_DOTS[stage]} />
        <div className="flex flex-1 flex-col gap-6 p-6">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">
              Check the label on your {VENTILATION_CONNECTOR}
            </h1>
            <p className="text-muted text-[17px] leading-6">
              {mismatchDiagnosis?.question ??
                `The connector we have on record for your home does not match the one your hub found. Look at the label on the back of the ${VENTILATION_CONNECTOR}.`}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <img
              src="/brand/onboarding/connector-mac-label.jpg"
              alt={`The label on the back of the ${VENTILATION_CONNECTOR}, with the MAC address marked`}
              className="w-full rounded-2xl"
            />
            {/* The label's own Serial Param line reads 57600/N/8/1, the
                factory default rather than what is configured. Left
                unexplained, that line misleads anyone using this same photo
                to debug a serial mismatch later. */}
            <p className="text-muted type-body-sm">
              The Serial Param line on the label is a factory default, not what your connector is
              set to. Read the MAC line only.
            </p>
          </div>
        </div>
        {onSkip && (
          <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-7">
            <PillButton
              label="Yes, it matches"
              busy={answering}
              onClick={() => answerMacMismatch(true)}
            />
            <PillButton
              label="No, it doesn't match"
              variant="secondary"
              withArrow={false}
              busy={answering}
              onClick={() => answerMacMismatch(false)}
            />
          </div>
        )}
      </div>
    );
  }

  // ── Stage 4: turbo-intro — about to run the unit at full speed ───────────
  if (stage === "turbo-intro") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="ventilation"
          stage={stage}
          title={PRODUCT_NAMES.ventilation}
          onBack={() => setStage("cable")}
        />
        <PhaseDots total={VENT_STAGE_TOTAL} activeIndex={STAGE_DOTS[stage]} />
        <div className="flex flex-1 flex-col items-center gap-10 px-8 pt-6">
          <FanHero spinning />
          <div className="flex flex-col gap-3 text-center">
            <h1 className="text-primary type-headline">We&apos;ll run the fan at full speed</h1>
            <p className="text-muted text-[17px] leading-6">
              Your ventilation will run flat out for a few seconds so you can hear it. Stand near
              the unit. We&apos;ll put it back exactly where it was afterwards.
            </p>
          </div>
          <div className="flex w-full flex-col items-center gap-3">
            {turboError && (
              <p role="alert" className="text-destructive text-center text-[15px]">
                {turboError}
              </p>
            )}
            <PillButton
              label="Run it now"
              busy={turbo.isPending}
              onClick={() => void activateTurbo()}
            />
            {/* The first failure gets the retry copy above and the assistant,
                which knows to say wait and try again. The skip only appears
                once a second try has failed too: a skip here flags the house
                for support, and one more tap is usually all it needed. */}
            {turboError && (
              <StuckHelp
                stepKey="ventilation"
                stage="turbo_failed"
                lastError={turboError}
                seed="I tapped Run it now and the app says the ventilation unit did not confirm the command. What should I do?"
                label="Ask what to do"
              />
            )}
            {turboError && onSkip && turboFailures >= TURBO_TRIES_BEFORE_SKIP && (
              <SkipFailedStep busy={skipping} onSkip={() => skipFailedTurbo()} />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Stage 5: turbo-listen — confirm the unit spun up ─────────────────────
  if (stage === "turbo-listen") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="ventilation"
          stage={stage}
          title={PRODUCT_NAMES.ventilation}
          onBack={() => setStage("turbo-intro")}
        />
        <PhaseDots total={VENT_STAGE_TOTAL} activeIndex={STAGE_DOTS[stage]} />
        <div className="flex flex-1 flex-col items-center gap-10 px-8 pt-6">
          <FanHero spinning />
          <div className="flex flex-col gap-3 text-center">
            <h1 className="text-primary type-headline">Listen for the fan</h1>
            <p className="text-muted text-[17px] leading-6">
              It should be noticeably louder than usual.
            </p>
          </div>
          <div className="flex w-full flex-col gap-4 px-5">
            <button
              type="button"
              onClick={confirmHeard}
              className="border-success bg-success-subtle flex items-center gap-4 rounded-2xl border-[1.5px] p-5 text-left active:opacity-90"
            >
              <div className="bg-success flex size-12 shrink-0 items-center justify-center rounded-3xl">
                <Ear className="size-6 text-white" />
              </div>
              <p className="text-success text-[17px] leading-6">Yes, I can hear it</p>
            </button>
            {/* Loop back so the resident can re-trigger TURBO and listen again. */}
            <button
              type="button"
              onClick={() => setStage("turbo-intro")}
              className="border-border bg-background flex items-center gap-4 rounded-2xl border p-5 text-left active:opacity-90"
            >
              <div className="bg-surface-subtle flex size-12 shrink-0 items-center justify-center rounded-3xl">
                <HelpCircle className="text-muted size-6" />
              </div>
              <p className="text-primary text-[17px] leading-6">I can&apos;t hear anything</p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Stage 6: done — the Nilan is online and responding ───────────────────
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
      <AppBar stepKey="ventilation" stage={stage} title={PRODUCT_NAMES.ventilation} />
      <PhaseDots total={VENT_STAGE_TOTAL} activeIndex={STAGE_DOTS[stage]} />
      <div className="flex flex-1 flex-col items-center gap-10 px-8 pt-6">
        <FanHero spinning={false} done />
        <div className="flex flex-col gap-3 text-center">
          <h1 className="text-primary type-headline">Ventilation connected</h1>
          <p className="text-muted text-[17px] leading-6">
            SmartClimate can reach your Nilan and control it.{" "}
            {previousFanMode
              ? `We've set the fan back to ${previousFanMode}, where you had it.`
              : "We've taken the fan back off full speed."}
          </p>
        </div>
        <div className="flex w-full flex-col items-center gap-3">
          {error && (
            <p role="alert" className="text-destructive text-center text-[15px]">
              {error}
            </p>
          )}
          <PillButton label="See what's next" busy={busy} onClick={onComplete} />
        </div>
      </div>
    </div>
  );
}
